import _ from 'lodash/fp'
import gql from 'graphql-tag'
import { all, call, put, takeLatest } from 'redux-saga/effects'

import { types, actions } from '../reducers/status'
import { getCurrentClubId } from './network'

import { apolloClient } from '@fe/business/src/apollo'

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

const SELF_INTERSECTIONS_QUERY = gql`
  query ClubSelfIntersections($clubId: ID!) {
    clubSelfIntersections(clubId: $clubId) {
      id
      elementName
    }
  }
`

const BOUNDARY_SELF_INTERSECTIONS_QUERY = gql`
  query ClubBoundarySelfIntersections($clubId: ID!) {
    clubBoundarySelfIntersections(clubId: $clubId)
  }
`

const GREENS_WITHIN_BOUNDARY_QUERY = gql`
  query ClubGreensWithinBoundary($clubId: ID!) {
    clubGreensWithinBoundary(clubId: $clubId) {
      holeId
      greenIds
    }
  }
`

function * loadSelfIntersectionsEffect (clubId) {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: SELF_INTERSECTIONS_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.clubSelfIntersections', response)
  } catch (error) {
    return { error }
  }
}

function * loadBoundarySelfIntersectionsEffect (clubId) {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: BOUNDARY_SELF_INTERSECTIONS_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.clubBoundarySelfIntersections', response)
  } catch (error) {
    return { error }
  }
}

function * loadGreensWithinBoundaryEffect (clubId) {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: GREENS_WITHIN_BOUNDARY_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.clubGreensWithinBoundary', response)
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function * loadFlow () {
  const clubId = yield call(getCurrentClubId)
  const [selfIntersections, boundarySelfIntersections, greensWithinBoundary] = yield all([
    call(loadSelfIntersectionsEffect, clubId),
    call(loadBoundarySelfIntersectionsEffect, clubId),
    call(loadGreensWithinBoundaryEffect, clubId)
  ])
  const payload = {
    selfIntersections,
    boundarySelfIntersections,
    greensWithinBoundary: _.keyBy('holeId', greensWithinBoundary)
  }
  if (selfIntersections.error || boundarySelfIntersections.error || greensWithinBoundary.error) {
    yield put(actions.loadError(payload))
  } else {
    yield put(actions.loadSuccess(payload))
  }

  yield put(actions.loadRequestDone())
}

export default function * () {
  return yield all([
    takeLatest(types.LOAD_REQUEST, loadFlow)
  ])
}
